import { createContext, FC, PropsWithChildren, useEffect } from 'react';
import { IdentificationMethod } from 'types/ui';
import { R } from 'config/app';
import { useNavigate } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import useCustomerQuery from 'ui/hooks/useClientQuery';
import useIdentificationManualMutation from 'ui/hooks/useIdentificationManualMutation';
import useIdentificationMethodQuery from 'ui/hooks/useIdentificationMethodQuery';
import useLoanAmount from './useLoanAmount';
import useProgress from './useProgress';
import useTestingTool from './useTestingTool';

export interface LoanApplicationProcessValue {
  progress: number;
  loanAmount: number;
  identificationMethod: IdentificationMethod | null;
  isFreshAttemptToCreateLoan: boolean;
  setLoanAmount: (value: number) => void;
  onlineBankIdentificationChosen: () => void;
}

export const LoanApplicationProcessContext =
  createContext<LoanApplicationProcessValue | null>(null);

const LoanApplicationProcessProvider: FC<PropsWithChildren> = (props) => {
  const progress = useProgress();
  const { loanAmount, setLoanAmount } = useLoanAmount();
  const [isFreshAttemptToCreateLoan] = useSessionStorage(
    'isFreshAttemptToCreateLoan',
    true,
  );

  const identificationMethodQuery = useIdentificationMethodQuery();
  const identificationManualMutation = useIdentificationManualMutation();
  const navigate = useNavigate();
  const customerQuery = useCustomerQuery();

  useTestingTool();

  useEffect(() => {
    if (identificationManualMutation.status === 'success')
      customerQuery.refetch();
  }, [identificationManualMutation.status]);

  const onlineBankIdentificationChosen = () => {
    navigate(R.FirstLoanTink);
  };

  return (
    <LoanApplicationProcessContext.Provider
      value={{
        progress,
        loanAmount,
        identificationMethod:
          identificationMethodQuery.identificationMethod || null,
        isFreshAttemptToCreateLoan,
        setLoanAmount,
        onlineBankIdentificationChosen,
      }}
    >
      {props.children}
    </LoanApplicationProcessContext.Provider>
  );
};

export default LoanApplicationProcessProvider;
